import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ColumnPreset } from '../components/common/ColumnSelector';

interface UseColumnVisibilityProps {
  columns: Array<{ id: string; alwaysVisible?: boolean }>;
  /** localStorage key. Required, and unique per table: sharing one means two
   *  tables overwrite each other's saved choice. */
  storageKey: string;
  /** Columns shown before the user picks. */
  defaultColumns: readonly string[];
  /** Presets offered by the column selector, when it offers any. */
  presets?: Record<string, ColumnPreset>;
}

const getDefaultColumns = (
  columns: UseColumnVisibilityProps['columns'],
  defaults: readonly string[]
) => {
  const known = new Set(columns.map(col => col.id));
  return defaults.filter(id => known.has(id));
};

export const useColumnVisibility = ({
  columns,
  storageKey,
  defaultColumns,
  presets = {},
}: UseColumnVisibilityProps) => {
  // Initialize with defaults or saved preferences
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return new Set(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
    }

    return new Set(getDefaultColumns(columns, defaultColumns));
  });

  // Save to localStorage whenever visibility changes
  useEffect(() => {
    try {
      const visibleArray = Array.from(visibleColumns);
      localStorage.setItem(storageKey, JSON.stringify(visibleArray));
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    }
  }, [visibleColumns, storageKey]);

  const toggleColumn = useCallback((columnId: string) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  }, []);

  const setVisibleColumnsFromList = useCallback((columnIds: string[]) => {
    setVisibleColumns(new Set(columnIds));
  }, []);

  const resetToDefault = useCallback(() => {
    setVisibleColumns(new Set(getDefaultColumns(columns, defaultColumns)));
  }, [columns, defaultColumns]);

  const applyPreset = useCallback((presetKey: string) => {
    const preset = presets[presetKey];
    if (!preset) return;

    if (preset.columns === 'all') {
      // Show all columns
      const allColumns = columns.map(col => col.id);
      setVisibleColumns(new Set(allColumns));
    } else if (Array.isArray(preset.columns)) {
      // Always include required columns (select, actions)
      const requiredColumns = columns
        .filter(col => col.alwaysVisible)
        .map(col => col.id);
      const presetColumns = new Set([...preset.columns, ...requiredColumns]);
      setVisibleColumns(presetColumns);
    }
  }, [columns, presets]);

  const isColumnVisible = useCallback((columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.alwaysVisible) {
      return true;
    }
    return visibleColumns.has(columnId);
  }, [columns, visibleColumns]);

  /** The same decision in the shape TanStack's `columnVisibility` expects, so
   *  every table stops rebuilding this map for itself. */
  const columnVisibility = useMemo(() => {
    const state: Record<string, boolean> = {};
    columns.forEach((column) => {
      state[column.id] = isColumnVisible(column.id);
    });
    return state;
  }, [columns, isColumnVisible]);

  return {
    visibleColumns,
    columnVisibility,
    toggleColumn,
    setVisibleColumns: setVisibleColumnsFromList,
    resetToDefault,
    applyPreset,
    isColumnVisible,
  };
};
